import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Typography from "@/Utility/Typography";
import Input from "@/Utility/Input/Input";
import Button from "@/Utility/Button/Button";
import Radio from "@/Utility/Radio/Radio";
import UsersRolesSettings from "./ProjectSettingsTabs/UsersRolesSettings";
import IssueTypesSettings from "./ProjectSettingsTabs/IssueTypesSettings";
import WorkflowStatusSettings from "./ProjectSettingsTabs/WorkflowStatusSettings";
import BoardSettings from "./ProjectSettingsTabs/BoardSettings";
import FieldsSettings from "./ProjectSettingsTabs/FieldsSettings";
import TimeTrackingSettings from "./ProjectSettingsTabs/TimeTrackingSettings";
import NotificationSettings from "./ProjectSettingsTabs/NotificationSettings";
import SegmentedTabs from "@/Utility/SegmentedTabs.jsx/SegmentedTabs";
import { PROJECT_SETTINGS_SEGMENT_TABS } from "./settings.mapper";
import { DEFAULT_PROJECT_SETTINGS } from "./projectSettingsMapper";
import { fetchProjectSettings, updateProjectSettings } from "@/Redux/Thunks/projectSettingsThunk";
import {
  fetchProjectById,
  updateProject,
  fetchProjectsData,
} from "@/Redux/Thunks/projectManagementThunk";
import { normalizeProjectFromApi } from "@/Pages/Projects/projectManagementNormalizers";
import {
  selectProjectSettingsData,
  selectProjectSettingsLoading,
  selectProjectSettingsSaving,
  selectProjectSettingsError,
} from "@/Redux/Slices/projectSettingsSlice";
import { seedWorkspaceProjectTitle } from "@/Redux/Slices/projectDashboardSlice";
import { showSuccess, showError } from "@/utils/toast";
import { ContentLoader } from "@/Utility/Loader";

const ProjectSettings = ({ projectSlug }) => {
  const dispatch = useDispatch();
  const settingsData = useSelector((s) => selectProjectSettingsData(s, projectSlug));
  const loading = useSelector((s) => selectProjectSettingsLoading(s, projectSlug));
  const saving = useSelector((s) => selectProjectSettingsSaving(s, projectSlug));
  const loadError = useSelector((s) => selectProjectSettingsError(s, projectSlug));

  const [activeTab, setActiveTab] = useState("general");
  const [projectName, setProjectName] = useState("");
  const [projectKey, setProjectKey] = useState("");
  const [savedProjectName, setSavedProjectName] = useState("");
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);
  const [savingProjectName, setSavingProjectName] = useState(false);

  const [projectStatus, setProjectStatus] = useState("active");
  const [projectVisibility, setProjectVisibility] = useState("org");

  const [notification, setNotification] = useState(DEFAULT_PROJECT_SETTINGS.notification);

  useEffect(() => {
    if (!projectSlug) return;
    dispatch(fetchProjectSettings(projectSlug));
  }, [dispatch, projectSlug]);

  useEffect(() => {
    if (!projectSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = await dispatch(fetchProjectById(projectSlug)).unwrap();
        if (cancelled) return;
        const p = normalizeProjectFromApi(raw?.data ?? raw);
        const name = p.projectName ?? "";
        const key = p.projectKey ?? "";
        setProjectName(name);
        setSavedProjectName(name);
        setProjectKey(key);
      } catch {
        if (!cancelled) {
          setProjectName("");
          setSavedProjectName("");
          setProjectKey("");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, projectSlug]);

  useEffect(() => {
    if (!settingsData?.general) return;
    setProjectStatus(settingsData.general.project_status ?? "active");
    setProjectVisibility(settingsData.general.visibility ?? "org");
  }, [settingsData]);

  useEffect(() => {
    if (!settingsData?.notification) return;
    setNotification({ ...settingsData.notification });
  }, [settingsData]);

  const generalDirty = useMemo(() => {
    if (!settingsData?.general) return false;
    return (
      projectStatus !== settingsData.general.project_status ||
      projectVisibility !== settingsData.general.visibility
    );
  }, [settingsData, projectStatus, projectVisibility]);

  const notificationDirty = useMemo(() => {
    if (!settingsData?.notification) return false;
    const a = JSON.stringify({ ...notification });
    const b = JSON.stringify({ ...settingsData.notification });
    return a !== b;
  }, [settingsData, notification]);

  const handleSaveGeneral = useCallback(async () => {
    if (!projectSlug) return;
    try {
      await dispatch(
        updateProjectSettings({
          projectId: projectSlug,
          body: {
            general: {
              project_status: projectStatus,
              visibility: projectVisibility,
            },
          },
        })
      ).unwrap();
      showSuccess("General settings saved");
    } catch (e) {
      showError(typeof e === "string" ? e : e?.message || "Failed to save settings");
    }
  }, [dispatch, projectSlug, projectStatus, projectVisibility]);

  const handleProjectNameEditToggle = useCallback(async () => {
    if (!projectSlug) return;
    if (!isEditingProjectName) {
      setIsEditingProjectName(true);
      return;
    }
    const trimmed = projectName.trim();
    if (!trimmed) {
      showError("Project name is required");
      return;
    }
    if (trimmed === savedProjectName) {
      setIsEditingProjectName(false);
      return;
    }
    setSavingProjectName(true);
    try {
      const raw = await dispatch(
        updateProject({ id: projectSlug, projectData: { name: trimmed } })
      ).unwrap();
      const p = normalizeProjectFromApi(raw?.data ?? raw);
      const nextName = p.projectName ?? trimmed;
      setProjectName(nextName);
      setSavedProjectName(nextName);
      dispatch(seedWorkspaceProjectTitle({ projectId: projectSlug, name: nextName }));
      showSuccess("Project name updated");
      setIsEditingProjectName(false);
    } catch (e) {
      showError(typeof e === "string" ? e : e?.message || "Failed to update project name");
    } finally {
      setSavingProjectName(false);
    }
    try {
      await dispatch(fetchProjectsData({ getAll: true, force: true })).unwrap();
    } catch {
      /* list refresh is best-effort */
    }
  }, [dispatch, projectSlug, isEditingProjectName, projectName, savedProjectName]);

  const handleSaveNotification = useCallback(async () => {
    if (!projectSlug) return;
    try {
      await dispatch(
        updateProjectSettings({
          projectId: projectSlug,
          body: { notification },
        })
      ).unwrap();
      showSuccess("Notification settings saved");
    } catch (e) {
      showError(typeof e === "string" ? e : e?.message || "Failed to save settings");
    }
  }, [dispatch, projectSlug, notification]);

  if (!projectSlug) {
    return (
      <Typography variant="text2" className="text-text-secondary">
        Open a project to manage settings.
      </Typography>
    );
  }

  return (
    <div>
      <SegmentedTabs
        tabs={PROJECT_SETTINGS_SEGMENT_TABS}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      {activeTab === "general" && (
        <ContentLoader loading={loading} className="min-h-[200px]">
          {loadError && !loading ? (
            <Typography variant="text2" className="text-red-600 py-4">
              {loadError}
            </Typography>
          ) : (
            <div className="bg-surface-primary border border-border-primary rounded-md shadow-sm p-6 space-y-8">
              <div className="space-y-2">
                <Typography
                  variant="text2"
                  component="label"
                  htmlFor="projectName"
                  className="font-semibold text-text-primary mb-0 block"
                >
                  Project Name
                </Typography>
                <div className="relative flex items-center">
                  <div className="relative flex-1">
                    <Input
                      type="text"
                      id="projectName"
                      placeholder="Project name"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      label=""
                      noWrapper
                      readOnly={!isEditingProjectName}
                      className={`border border-border-primary rounded-md px-3 py-3 text-text-primary bg-surface-primary w-full ${
                        !isEditingProjectName ? "bg-gray-100 cursor-default" : ""
                      }`}
                    />
                    <Button
                      type="button"
                      variant={isEditingProjectName ? "secondary" : "primary"}
                      size="sm"
                      disabled={savingProjectName}
                      onClick={handleProjectNameEditToggle}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-[30px] px-4 rounded-md !leading-none"
                    >
                      {savingProjectName ? "Saving…" : isEditingProjectName ? "Done" : "Edit"}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Typography
                  variant="text2"
                  component="label"
                  htmlFor="projectKey"
                  className="font-semibold text-text-primary mb-0 block"
                >
                  Project Key
                </Typography>
                <Input
                  type="text"
                  id="projectKey"
                  placeholder="Project key"
                  value={projectKey}
                  label=""
                  noWrapper
                  readOnly
                  className="border border-border-primary rounded-md px-3 py-3 text-text-primary bg-gray-100 cursor-default w-full"
                />
              </div>

              <div className="space-y-3">
                <Typography variant="text2" className="font-semibold text-text-primary">
                  Project Owner
                </Typography>

                <Radio
                  name="status"
                  value="active"
                  checked={projectStatus === "active"}
                  onChange={setProjectStatus}
                  label="Active"
                />

                <Radio
                  name="status"
                  value="archived"
                  checked={projectStatus === "archived"}
                  onChange={setProjectStatus}
                  label="Archived"
                />
              </div>

              <div className="space-y-3">
                <Typography variant="text2" className="font-semibold text-text-primary">
                  Project Visibility
                </Typography>

                <Radio
                  name="visibility"
                  value="private"
                  checked={projectVisibility === "private"}
                  onChange={setProjectVisibility}
                  label="Private (only members)"
                />

                <Radio
                  name="visibility"
                  value="org"
                  checked={projectVisibility === "org"}
                  onChange={setProjectVisibility}
                  label="Organization-wide"
                />
              </div>

              <div>
                <Button
                  variant="primary"
                  size="md"
                  className="px-6"
                  disabled={!generalDirty || saving || loading}
                  onClick={handleSaveGeneral}
                >
                  {saving ? "Saving…" : "Save Changes"}
                </Button>
              </div>
            </div>
          )}
        </ContentLoader>
      )}

      {activeTab === "users_roles" && <UsersRolesSettings />}
      {activeTab === "issue_types" && <IssueTypesSettings />}
      {activeTab === "workflow_status" && <WorkflowStatusSettings />}
      {activeTab === "board_settings" && <BoardSettings />}
      {activeTab === "fields" && <FieldsSettings />}
      {activeTab === "time_tracking" && <TimeTrackingSettings />}
      {activeTab === "notification" && (
        <ContentLoader loading={loading} className="min-h-[200px]">
          {loadError && !loading ? (
            <Typography variant="text2" className="text-red-600 py-4">
              {loadError}
            </Typography>
          ) : (
            <NotificationSettings
              value={notification}
              onChange={setNotification}
              onSave={handleSaveNotification}
              saveDisabled={!notificationDirty}
              saving={saving}
            />
          )}
        </ContentLoader>
      )}
    </div>
  );
};

export default ProjectSettings;
